import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useUserStore } from '/src/context';
import { monthNames } from '/src/lib/constants';
import { ChartColors, DateRange, Nullable } from '/src/lib/types';
import { ColorScale, convertCamelCase, currency, dataSorter, getBarWidth, getWidth, toCamelCase } from '/src/utils';

// alias to stop TS errors
const { d3 } = window as any;

export type BarChartProps = {
  /**
   * The actual data for the chart to use
   */
  data: any;
  /**
   * Lets you look at data from other years
   */
  dateRange: DateRange;
  /**
   * Optional array of category names (other than the default three)
   */
  categoryNames?: string[];
  /**
   * Colors to use when filling in the bar chart
   */
  colorRange: ChartColors;
  /**
   * How long it takes for the bars to animate in/out
   */
  duration?: number;
  /**
   * Whether to show the popup tooltip when the user hovers over the bars
   */
  showTooltip?: boolean;
  /**
   * Controls whether the data sorts highest-lowest or lowest-highest
   */
  sortReverse: boolean;
  /**
   * Sorts data by given property (e.g. amount, value, etc.)
   */
  sortValue: string;
};

export const BarChart: React.FC<BarChartProps> = observer(
  ({
    data,
    dateRange,
    categoryNames = ['advances', 'principalPayments', 'revenue'],
    colorRange,
    duration = 500,
    showTooltip = true,
    sortReverse,
    sortValue,
  }) => {
    /** Context **/
    const userStore = useUserStore();

    /** Refs **/
    let element: Nullable<HTMLDivElement> = null;
    const barChart = useRef<any>(null);
    const tip = useRef<any>(null);

    /** State **/
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [categoryFilter, setCategoryFilter] = useState('');

    /** Computed **/
    const t = useMemo(() => d3.transition().duration(duration), [duration]);
    const colors = useMemo(() => new ColorScale(colorRange).colors, [colorRange]);

    // Set up data array for the grouped bars
    const chartData = useMemo(() => {
      let y0 = 0;
      // let y1 = 0;
      let arrayData: any[] = [];

      // Loop through each month
      data?.forEach(function (d: any) {
        arrayData = [];
        for (const category in d.items) {
          y0 = 0;
          if (d.items[category] && d.items[category].length) {
            d.items[category] = dataSorter(d.items[category], sortValue);

            if (sortReverse) d.items[category].reverse();

            for (const item in d.items[category]) {
              d.items[category][item].y0 = y0;
              d.items[category][item].y1 = y0 += +d.items[category][item].value;
            }
          }

          arrayData.push(d.items[category]);
        }

        d.arrayData = arrayData;
      });

      return data;
    }, [data]);

    /** Methods **/

    /***** FILTERS *****/

    const filterStacks = (category?: string) => {
      const chart = barChart.current;
      if (!chart) return;

      // Set up variables
      const newFilter = category || categoryFilter ? `.${category || categoryFilter}` : '';

      // If there are any filters checked...
      if (newFilter) {
        let first = true;

        // Select the old bars
        if (first) {
          chart.oldBars = chart.month.selectAll(`:not(${newFilter})`);
          first = false;
        } else {
          chart.oldBars.selectAll(`:not(${newFilter})`);
        }

        // Select the new bars
        chart.newBars = chart.month.selectAll(newFilter);
      } else {
        chart.oldBars = chart.newBars;
        chart.newBars = chart.month.selectAll('rect');
      }

      // Get max value of stacked bars
      const max = getMaxHeight(categoryFilter);

      // Update Y axis
      chart.y.domain([0, max]);
      chart.yAxis.scale(chart.y);
      chart.svg.select('.y.axis').remove();
      chart.svg
        .append('g')
        .attr('class', 'y axis')
        .call(chart.yAxis)
        .append('text')
        .attr('transform', 'rotate(-90)')
        .attr('y', 6)
        .attr('dy', '.71em')
        .attr('class', 'md-caption')
        .style('text-anchor', 'end')
        .text('Amount');

      // Animate old bars out
      if (chart.oldBars?._groups?.length) {
        chart.oldBars
          .transition(t)
          .attr('height', '0')
          .attr('y', (d: any) => chart.y(d.y0));
      }

      // Animate the new bars in
      if (chart.newBars?._groups?.length) {
        chart.newBars
          .transition(t)
          .attr('height', (d: any) => chart.y(d.y0) - chart.y(d.y1))
          .attr('y', (d: any) => chart.y(d.y1));
      }
    };

    const drawLegend = () => {
      const chart = barChart.current;

      chart.legend = chart.svg
        .selectAll('.legend')
        .data(categoryNames.slice())
        .enter()
        .append('g')
        .attr('class', 'legend')
        .attr('transform', (_: any, i: number) => 'translate(0,' + i * 25 + ')');

      let yLegend = -10;

      chart.legend
        .append('rect')
        .attr('class', (d: any) => d)
        .attr('x', chart.width - 25)
        .attr('y', () => {
          yLegend += 10;
          return yLegend;
        })
        .attr('width', 30)
        .attr('height', 30)
        .style('fill', (d: any) => colorRange[d][0])
        .on('mouseover', (d: any) => d3.selectAll('.' + d).style('opacity', '0.9'))
        .on('mouseleave', (d: any) => d3.selectAll('.' + d).style('opacity', '1'))
        .on('click', (d: any) => {
          // setFilters({ ...filters, category: categoryFilter === d ? '' : d });
          setCategoryFilter(categoryFilter === d ? '' : d);
          filterStacks(categoryFilter === d ? '' : d);
          drawLegend();
        });

      yLegend = 5;

      chart.legend
        .append('text')
        .attr('x', chart.width - 31)
        .attr('y', () => {
          yLegend += 10;
          return yLegend;
        })
        .attr('dy', '.35em')
        .attr('class', (d: any) => (categoryFilter === d ? 'legend-title medium' : 'legend-title thin'))
        .style('text-anchor', 'end')
        .text((d: any) =>
          d === 'revenue' ? (userStore.isAdmin || userStore.isLender ? 'Revenue' : 'Costs') : convertCamelCase(d)
        )
        .on('click', (d: any) => {
          // setFilters({ ...filters, category: categoryFilter === d ? '' : d });
          setCategoryFilter(categoryFilter === d ? '' : d);
          filterStacks(categoryFilter === d ? '' : d);
          drawLegend();
        });

      barChart.current = chart;
    };

    const drawBars = () => {
      const chart = barChart.current;

      chart.month = chart.svg
        .append('g')
        .selectAll('g')
        .data(data)
        .enter()
        .append('g')
        .attr('class', (d: any) => {
          const groupMonth = new Date(d.month).getMonth();
          return 'g month ' + monthNames[groupMonth].toLowerCase();
        })
        .attr('transform', (d: any) => {
          const items = Object.values(d.items);
          let offset = chart.xGroupBand;
          const count = items.length;
          const barWidth = count * chart.xGroupBand;
          if (count > 1) offset = barWidth / 2;

          return 'translate(' + (chart.margins.xAxis + chart.xScale(d.month) - offset) + ', 0)';
        });

      chart.stack = chart.month
        .selectAll('g')

        // Begin sorting by categories
        .data((d: any) => d.arrayData)
        .enter()
        .append('g')
        .attr('class', (d: any) => {
          // console.log('[drawBars] sorting datum: %O', d);
          if (d.length) return 'g category ' + d[0].category;
        })
        .selectAll('rect')

        // Then sort by company
        .data((d: any) => d)
        .enter()
        .append('rect')
        .attr('class', (d: any) =>
          `${d.category} ${toCamelCase(d.company)} ${toCamelCase(d.loan)} ${d.lenders
            .map((lender: string) => toCamelCase(lender))
            .join(' ')}`.trimEnd()
        )
        .attr('width', chart.xGroupBand)
        .attr('x', (d: any) => chart.x1(d.category))
        .attr('height', (d: any) => {
          const barHeight = chart.y(d.y0) - chart.y(d.y1);
          return barHeight < 0 ? 0 : barHeight;
        })
        .attr('y', (d: any) => chart.y(d.y1))
        .style('fill', (d: any) => colors[d.category](d.company))
        .style('cursor', 'pointer')
        .on('mouseover', (d: any) => {
          if (showTooltip) {
            tip.current
              .style('opacity', 1)
              .html(
                `<md-card class="d3-tooltip" layout="column" layout-padding layout-align="center center" style="background: #FFF;">
                  <span flex class="medium" style="border-bottom: 1px solid #000;">${d.company}</span>
                  <span flex>${currency.format(d.value)}</span>
                </md-card>`
              )
              .style('left', d3.event.pageX + 'px')
              .style('top', d3.event.pageY + 'px');
          }
        })
        .on('mouseleave', () => {
          tip.current.style('opacity', 0);
        });

      barChart.current = chart;
    };

    /***** DRAW CHART *****/
    const drawChart = (element: any, data: any) => {
      if (!element || !data) return;

      d3.select(element).selectAll('*').remove();

      // Create chart object
      // const chart: Record<string, any> = {};
      const chart: Record<string, any> = {};

      // Define SVG dimensions
      chart.margins = {
        top: 20,
        right: 70,
        bottom: 50,
        left: 50,
        xAxis: 75,
      };
      chart.width = getWidth(element);
      if (chart.width) chart.width = chart.width - chart.margins.left - chart.margins.right;
      chart.height = 472.5 - chart.margins.top - chart.margins.bottom;

      // Create SVG
      chart.svg = d3
        .select(element)
        .append('svg')
        .attr('class', 'totalBarChart-svg')
        .attr('width', chart.width + chart.margins.left + chart.margins.right)
        .attr('height', chart.height + chart.margins.top + chart.margins.bottom)
        .append('g')
        .attr('transform', 'translate(' + chart.margins.left + ',' + chart.margins.top + ')');

      // chart.svg.call(tip.current).call(totalTip.current);

      // Month Axis (x)
      chart.xScale = d3.scaleTime().range([0, chart.width]).domain([dateRange.start, dateRange.end]);
      chart.xAxis = d3.axisBottom(chart.xScale).ticks(12).tickFormat(d3.timeFormat('%b'));

      chart.svg
        .append('g')
        .attr('class', 'x axis')
        .attr('transform', 'translate(' + chart.margins.xAxis + ',' + (chart.height + 6) + ')')
        .call(chart.xAxis);

      // Groups Axis (x)
      chart.xGroupBand = getBarWidth(chart.width, 40);
      chart.x1 = d3
        .scaleBand()
        .domain(categoryNames)
        .range([0, chart.xGroupBand * 3]);

      // Amount Axis (y)
      chart.y = d3.scaleLinear().rangeRound([chart.height, 0]);
      chart.y.domain([
        0,
        d3.max(data, function (d: any) {
          return d.total;
        }),
      ]);
      // y1 = d3.scaleLinear().rangeRound([chart.height, 0]);
      chart.y.domain([0, getMaxHeight()]);
      chart.yAxis = d3.axisLeft(chart.y).tickFormat(d3.format('.2s'));

      chart.svg
        .append('g')
        .attr('class', 'y axis')
        .call(chart.yAxis)
        .append('text')
        .attr('transform', 'rotate(-90)')
        .attr('y', 6)
        .attr('dy', '.71em')
        .attr('class', 'md-caption')
        .style('text-anchor', 'end')
        .text('Amount');

      barChart.current = chart;

      /***** LEGEND *****/
      drawLegend();

      /***** DRAW BARS *****/
      drawBars();

      /***** FILTER STACKS *****/
      filterStacks();
    };

    /**
     * Calculate max bar height
     */
    const getMaxHeight = function (filter?: string) {
      const response: any[] = [];
      data.forEach(function (d: any) {
        for (const category in d) {
          if (d[category]) {
            for (const name in categoryNames) {
              if (category === categoryNames[name]) {
                if (filter) {
                  if (category === filter) response.push(d[category]);
                } else {
                  response.push(d[category]);
                }
              }
            }
          }
        }
      });
      return d3.max(response);
    };

    const createTip = () => {
      // tip.current = d3Tip()
      tip.current = d3
        .select('body')
        .append('div')
        .attr('class', 'd3-tooltip')
        .style('opacity', 0)
        .style('position', 'absolute')
        .style('pointer-events', 'none');
    };

    /** Effects **/

    useEffect(() => {
      window.onresize = () => setWindowWidth(window.innerWidth);

      createTip();

      return () => {
        window.onresize = null;
        d3.select(element).selectAll('*').remove();
      };
    }, []);

    useEffect(() => {
      if (element && chartData) drawChart(element, chartData);
    }, [element, chartData, windowWidth]);

    // need one just for category filters, because we're not going to redraw the whole chart
    useEffect(() => {
      if (element && chartData) drawChart(element, chartData);
    }, [categoryFilter]);

    /** Render **/

    return (
      <Box justifyContent="center" alignItems="center" className="pad-top-bottom" display="flex" flex={1}>
        <div
          ref={(node) => {
            if (!element) element = node;
          }}
          id="total-bar-chart-container"
        >
          <CircularProgress className="md-primary" />
        </div>
      </Box>
    );
  }
);
